import { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { compact, isEqual } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';

import Page from '../page/Page';
import PageHeader from '../page-header/PageHeader';
import { createPanelComponent } from '../panel/PanelRegistry';
import PanelContainer from '../panel-container/PanelContainer';
import { NavigationMenu, PanelCatalog } from '../../side-nav';
import DashboardActions from './DashboardActions';
import FilterContainer from '../filter-container/FilterContainer';
import { createFilterComponent } from '../filter/FilterRegistry';
import ErrorMessage from '../../error-message/ErrorMessage';
import { useDashboardContext, NEW_DASHBOARD_ID, DASHBOARD_ROW_HEIGHT } from './DashboardContext';
import { getLogger } from '../../../utilities/UseLog';
import { isNotFoundError } from '../../../utilities/RequestUtilities';
import { ConfirmNavigationDialog } from '../../dialogs/ConfirmNavigationDialog';
import { useErrorWrapper } from '../../../services/FormErrorService';
import { INFO, createAlertConfig } from '../../../stores/AlertsStore';
import { useDefaultPage } from '../../side-nav/DefaultPage';
import { useDashboardMenu } from '../../../stores/DashboardMenuStore';
import { useIsSysAdminUser } from '../../../stores/SessionStore';
import Actions from '../actions/Actions';
import Link from '../../links/Link';
import { JumpSettings as JumpSettingsIcon } from '../../icon';

const log = getLogger('Dashboard');

function convertLayoutToProps(layout) {
    const { x, y, w: width, h: height } = layout;

    return {
        x,
        y,
        width,
        height,
    };
}

function createFilterComponents(filters) {
    return (
        compact(
            filters?.map(({ id, required = false }) => {
                try {
                    return createFilterComponent(id, {
                        required,
                    });
                } catch (error) {
                    log.error(error);
                }

                return undefined;
            })
        ) || []
    );
}

function createDashboardPanelComponent(panelTypeName, props) {
    return createPanelComponent(panelTypeName, {
        ...props,
        stateSummaryOptions: {
            show: true,
        },
    });
}

function createPanelComponents(panels) {
    return (
        panels?.map((panel) => {
            const layoutProps = convertLayoutToProps(panel.layout);
            try {
                return createDashboardPanelComponent(panel.id, {
                    key: panel.dashboardId,
                    dashboardId: panel.dashboardId,
                    hideTitle: panel.showTitle === false,
                    overrides: panel.overrides,
                    ...layoutProps,
                });
            } catch (error) {
                return (
                    <ErrorMessage
                        key={panel.dashboardId}
                        error={error}
                        componentStack={error.stack}
                        title="Unknown Panel"
                        {...layoutProps}
                    />
                );
            }
        }) || []
    );
}

export default function Dashboard() {
    const { state: locationState } = useLocation();
    const { pageId } = useParams();
    const loadedPageId = useRef();
    const { error, loading, editing, dragging, droppingItem, dashboard, dirtyDashboard, dashboardActions, saving } =
        useDashboardContext();
    const [dashboardMenu] = useDashboardMenu();
    const previousFilters = useRef([]);
    const [filterComponents, setFilterComponents] = useState([]);
    const [panelComponents, setPanelComponents] = useState([]);
    const { errorWrapper } = useErrorWrapper();
    const { defaultPage } = useDefaultPage();
    const [isSysAdminUser] = useIsSysAdminUser();

    const load = useRef();
    load.current = (id) => {
        // Only load the dashboard if it changed.
        if (dashboard?.id !== id) dashboardActions.load(id, locationState?.editing || false);
    };

    const reset = useRef();
    reset.current = () => {
        // Clear the current set of filters and panels when the dashboard is reset.
        previousFilters.current = [];
        setFilterComponents([]);
        setPanelComponents([]);
        dashboardActions.reset();
    };

    const createDashboardPanel = useRef();
    createDashboardPanel.current = (panelTypeName, props) => {
        const dashboardId = `${panelTypeName}-${dirtyDashboard.panels.length}`;
        return createDashboardPanelComponent(panelTypeName, {
            ...props,
            key: dashboardId,
            dashboardId,
        });
    };

    const handleLayoutChange = useRef();
    handleLayoutChange.current = (newPanels) => {
        // Extract the panels from the new layout and update the DashboardContext panel state.
        const newDashboardPanels = newPanels.map((layoutItem) => {
            const { dashboardId, typeName, hideTitle, overrides } = layoutItem.panel.props;
            return {
                id: typeName,
                dashboardId,
                showTitle: !hideTitle,
                overrides,
                layout: layoutItem.layout,
            };
        });

        log.debug('Layout changed');
        log.debugTable(newDashboardPanels);

        dashboardActions.setPanels(newDashboardPanels);
    };

    // Load the dashboard configuration when the page ID changes.
    useEffect(() => {
        load.current(pageId);
        loadedPageId.current = pageId;
        return () => reset.current();
    }, [pageId]);

    // Create new filter components when the dashboard filters change.
    useEffect(() => {
        // Only create new filter components if we have filters in the dashboard, and they are
        // different from the previous set of filters.
        if (!dirtyDashboard?.filters || isEqual(dirtyDashboard?.filters, previousFilters.current)) return;

        log.debug('Filters changed:');
        log.debugTable(dirtyDashboard?.filters);

        previousFilters.current = dirtyDashboard?.filters;

        setFilterComponents(createFilterComponents(dirtyDashboard?.filters));
    }, [dirtyDashboard?.filters]);

    // Create new panel components when the dashboard panels change.
    useEffect(() => {
        setPanelComponents(createPanelComponents(dashboard?.panels));
    }, [dashboard?.panels]);

    /**
     * This use effect acts as the 404 catcher for the react routes. This is because of the way we have our routes structured,
     * this ends up acting as a catch all, so if you need to do some not found processing, this is your spot.
     */
    useEffect(() => {
        if (!dashboardMenu.initialized) return;

        if (error) {
            // This means the user is trying to access a dashboard OR page that doesn't exist or that they don't have access to.
            if (isNotFoundError(error)) {
                return errorWrapper(
                    () =>
                        createAlertConfig(
                            INFO,
                            t`That page you attempted to access does not exist or has been removed.`
                        ),
                    `/${defaultPage}`
                )({});
            }

            return <ErrorMessage error={error} componentStack={error.stack || ''} />;
        }
    }, [error, defaultPage, dashboardMenu, errorWrapper]);

    // This is a hack to workaround a filter state initialization timing issue.  Since the `Dashboard`
    // component is re-used from dashboard to dashboard, the `filterComponents` state variable will
    // contain the previous dashboard's filters when a new dashboard is being loaded.  This means that
    // the `FilterContainer` may incorrectly initialize the filter state using the previous dashboard's
    // filters instead of the new dashboard's filters.  This check ensures that we've loaded the new
    // dashboard before rendering, which avoids rendering with stale filters from the previous dashboard.
    if (loadedPageId.current !== pageId) return null;

    return (
        <>
            {editing && (
                <NavigationMenu
                    title="Panel Catalog"
                    id="panel-catalog"
                    showBack={false}
                    // only grab focus if we're NOT at the very beginning of creating a new dashboard.
                    // In that case, the title text input will grab focus.
                    render={() => <PanelCatalog grabFocus={dashboard.id !== NEW_DASHBOARD_ID} />}
                />
            )}
            <Page id={pageId}>
                <PageHeader
                    title={dirtyDashboard?.title}
                    actions={<DashboardActions pageTitle={dirtyDashboard?.title} />}
                    titleActions={
                        isSysAdminUser && (
                            <Actions>
                                <Link
                                    to={`/admin/dashboards/${dashboard?._id}`}
                                    title={t`Edit Dashboard configuration`}
                                >
                                    <JumpSettingsIcon fontSize="small" />
                                </Link>
                            </Actions>
                        )
                    }
                >
                    {
                        // Wait until the dashboard is loaded before rendering the FilterContainer.  This prevents the
                        // filters from being intialized prematurely.
                    }
                    {!loading && dirtyDashboard && (
                        <FilterContainer dashboardSavedFilterState={dirtyDashboard.savedFilterState}>
                            {filterComponents}
                        </FilterContainer>
                    )}
                </PageHeader>
                <PanelContainer
                    loading={loading}
                    editing={editing}
                    dragging={dragging}
                    rowHeight={DASHBOARD_ROW_HEIGHT}
                    droppingItem={droppingItem}
                    panelProvider={createDashboardPanel.current}
                    onLayoutChange={handleLayoutChange.current}
                    onDraggingChange={dashboardActions.setDragging}
                >
                    {panelComponents}
                </PanelContainer>
                <ConfirmNavigationDialog confirmWhen={editing && !saving} />
            </Page>
        </>
    );
}
